import { useCallback, useMemo } from "react";
import { Godaam } from "../utils/Godaam";
import { z } from "zod";
import { isEmpty } from "lodash-es";
import { useAuth } from "@certa/auth/AuthenticationProvider";

const FeatureHighlightSchema = z.object({
  featureName: z.string(),
  timestamp: z.string()
});

const UserFeatureHighlightsSchema = z.record(
  z.string(),
  z.array(FeatureHighlightSchema)
);

type FeatureHighlight = z.infer<typeof FeatureHighlightSchema>;
type UserFeatureHighlights = z.infer<typeof UserFeatureHighlightsSchema>;

export const FEATURE_HIGHLIGHTS_KEY = "featureHighlights";

export const useFeatureHighlights = () => {
  // Initialize user id with empty because we know that user id is always present
  // for logged in users
  const { userId } = useAuth();

  const isFeatureHighlighted = useCallback(
    (featureTag: FeaturesWithHighlights): boolean => {
      const highlights = getFeatureHighlights();
      // when we log out we removed the user id from local storage,
      // which falsely give us false positive for feature highlight
      // so we return true to not shown the feature highlight again
      if (!userId) {
        return true;
      }
      const userHighlights = highlights[userId] ?? [];
      return userHighlights.some(
        highlight => highlight.featureName === featureTag
      );
    },
    [userId]
  );

  const markFeatureAsHighlighted = useCallback(
    (featureTag: FeaturesWithHighlights) => {
      if (!userId) {
        return;
      }
      const highlights = getFeatureHighlights();
      const userHighlights = highlights[userId] || [];

      // Add new highlight if it doesn't exist
      if (
        !userHighlights.some(highlight => highlight.featureName === featureTag)
      ) {
        const newHighlight: FeatureHighlight = {
          featureName: featureTag,
          timestamp: new Date().toISOString()
        };

        highlights[userId] = [...userHighlights, newHighlight];
        Godaam[FEATURE_HIGHLIGHTS_KEY] = !isEmpty(highlights)
          ? JSON.stringify(highlights)
          : null;
      }
    },
    [userId]
  );

  return useMemo(
    () => ({
      isFeatureHighlighted,
      markFeatureAsHighlighted
    }),
    [isFeatureHighlighted, markFeatureAsHighlighted]
  );
};

type FeaturesWithHighlights = "recall_ai_welcome_upload_modal";

const getFeatureHighlights = (): UserFeatureHighlights => {
  try {
    const stringifyHighlights = Godaam[FEATURE_HIGHLIGHTS_KEY];
    // we will have 'null' in Godaam[FEATURE_HIGHLIGHTS_KEY] due to Godaam.clear() function
    // so we need to handle that case using safeParse
    if (!stringifyHighlights) {
      return {};
    }
    const result = UserFeatureHighlightsSchema.safeParse(
      JSON.parse(stringifyHighlights)
    );
    if (result.success) {
      return result.data;
    }
    console.error("Invalid feature highlights data:", result.error);
    return {};
  } catch (error) {
    console.error("Error getting feature highlights:", error);
    return {};
  }
};
